/**
 * Outil « Atome et noyau » (Boîte à outils, physique-chimie 3e → Tle) — logique PURE.
 *
 * Deux parties :
 * - Atome (3e, 2nde) : on choisit l'élément (Z ≤ 18), l'isotope, le nombre d'électrons ;
 *   composition, charge (atome neutre ou ion), écriture conventionnelle, couches et
 *   configuration électronique (2nde), électrons de valence, place dans le tableau, ion stable
 *   prévu, masse ≈ A × m(nucléon).
 * - Noyau (2nde, Tle) : un noyau ᴬ_Z X qu'on fait se désintégrer (α, β⁻, β⁺) ; conservation de A
 *   et de Z, noyau fils, équation.
 */

import { frNum, mcqOptions, pick, retryMcq, type Mcq, type Rng } from '../../ui/mcq';

export const fr = frNum;
const NB = String.fromCharCode(0xa0);

export type Part = 'atom' | 'nucleus';

export const PARTS: ReadonlyArray<{ id: Part; label: string; ranks: readonly number[] }> = [
  { id: 'atom', label: 'Atome', ranks: [4, 5] },
  { id: 'nucleus', label: 'Noyau', ranks: [5, 7] },
];

export function partsFor(rank: number | null) {
  return PARTS.filter((p) => rank === null || p.ranks.includes(rank));
}

/* ---------- éléments ---------- */

const ELEMENTS: ReadonlyArray<readonly [string, string]> = [
  ['H', 'hydrogène'],
  ['He', 'hélium'],
  ['Li', 'lithium'],
  ['Be', 'béryllium'],
  ['B', 'bore'],
  ['C', 'carbone'],
  ['N', 'azote'],
  ['O', 'oxygène'],
  ['F', 'fluor'],
  ['Ne', 'néon'],
  ['Na', 'sodium'],
  ['Mg', 'magnésium'],
  ['Al', 'aluminium'],
  ['Si', 'silicium'],
  ['P', 'phosphore'],
  ['S', 'soufre'],
  ['Cl', 'chlore'],
  ['Ar', 'argon'],
  ['K', 'potassium'],
  ['Ca', 'calcium'],
  ['Sc', 'scandium'],
  ['Ti', 'titane'],
  ['V', 'vanadium'],
  ['Cr', 'chrome'],
  ['Mn', 'manganèse'],
  ['Fe', 'fer'],
  ['Co', 'cobalt'],
  ['Ni', 'nickel'],
  ['Cu', 'cuivre'],
  ['Zn', 'zinc'],
  ['Ga', 'gallium'],
  ['Ge', 'germanium'],
  ['As', 'arsenic'],
  ['Se', 'sélénium'],
  ['Br', 'brome'],
  ['Kr', 'krypton'],
  ['Rb', 'rubidium'],
  ['Sr', 'strontium'],
  ['Y', 'yttrium'],
  ['Zr', 'zirconium'],
  ['Nb', 'niobium'],
  ['Mo', 'molybdène'],
  ['Tc', 'technétium'],
  ['Ru', 'ruthénium'],
  ['Rh', 'rhodium'],
  ['Pd', 'palladium'],
  ['Ag', 'argent'],
  ['Cd', 'cadmium'],
  ['In', 'indium'],
  ['Sn', 'étain'],
  ['Sb', 'antimoine'],
  ['Te', 'tellure'],
  ['I', 'iode'],
  ['Xe', 'xénon'],
  ['Cs', 'césium'],
  ['Ba', 'baryum'],
  ['La', 'lanthane'],
  ['Ce', 'cérium'],
  ['Pr', 'praséodyme'],
  ['Nd', 'néodyme'],
  ['Pm', 'prométhium'],
  ['Sm', 'samarium'],
  ['Eu', 'europium'],
  ['Gd', 'gadolinium'],
  ['Tb', 'terbium'],
  ['Dy', 'dysprosium'],
  ['Ho', 'holmium'],
  ['Er', 'erbium'],
  ['Tm', 'thulium'],
  ['Yb', 'ytterbium'],
  ['Lu', 'lutécium'],
  ['Hf', 'hafnium'],
  ['Ta', 'tantale'],
  ['W', 'tungstène'],
  ['Re', 'rhénium'],
  ['Os', 'osmium'],
  ['Ir', 'iridium'],
  ['Pt', 'platine'],
  ['Au', 'or'],
  ['Hg', 'mercure'],
  ['Tl', 'thallium'],
  ['Pb', 'plomb'],
  ['Bi', 'bismuth'],
  ['Po', 'polonium'],
  ['At', 'astate'],
  ['Rn', 'radon'],
  ['Fr', 'francium'],
  ['Ra', 'radium'],
  ['Ac', 'actinium'],
  ['Th', 'thorium'],
  ['Pa', 'protactinium'],
  ['U', 'uranium'],
  ['Np', 'neptunium'],
  ['Pu', 'plutonium'],
];

export const MAX_Z = ELEMENTS.length;
export const symbolOf = (Z: number) => ELEMENTS[Z - 1]?.[0] ?? '?';
export const nameOf = (Z: number) => ELEMENTS[Z - 1]?.[1] ?? 'inconnu';

/** isotopes connus (naturels, ou radioactifs très utilisés) des éléments Z ≤ 18 ; le 1er est le plus abondant */
export const ISOTOPES: Record<number, readonly number[]> = {
  1: [1, 2, 3],
  2: [4, 3],
  3: [7, 6],
  4: [9],
  5: [11, 10],
  6: [12, 13, 14],
  7: [14, 15],
  8: [16, 17, 18],
  9: [19, 18],
  10: [20, 21, 22],
  11: [23, 22],
  12: [24, 25, 26],
  13: [27],
  14: [28, 29, 30],
  15: [31, 32],
  16: [32, 33, 34],
  17: [35, 37],
  18: [40, 36, 38],
};

/** nucléon (proton ou neutron) : 1,67 × 10⁻²⁷ kg, comme la carte */
export const M_NUCLEON = 1.67e-27;

/* ---------- atome ---------- */

const SUB = [
  { n: 1, l: 's', cap: 2 },
  { n: 2, l: 's', cap: 2 },
  { n: 2, l: 'p', cap: 6 },
  { n: 3, l: 's', cap: 2 },
  { n: 3, l: 'p', cap: 6 },
] as const;

const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
export const sup = (n: number) => String(n).replace(/\d/g, (d) => SUP[Number(d)]!);

/** sous-couches remplies dans l'ordre 1s 2s 2p 3s 3p (programme de 2nde : jusqu'à 18 électrons) */
export function configuration(e: number): Array<{ n: number; l: string; k: number }> {
  const out: Array<{ n: number; l: string; k: number }> = [];
  let left = Math.max(0, Math.min(18, e));
  for (const s of SUB) {
    if (left <= 0) break;
    const k = Math.min(s.cap, left);
    out.push({ n: s.n, l: s.l, k });
    left -= k;
  }
  return out;
}

export const configText = (e: number) =>
  configuration(e)
    .map((s) => `${s.n}${s.l}${sup(s.k)}`)
    .join(' ');

/** électrons par couche (K, L, M) */
export function shells(e: number): number[] {
  const c = configuration(e);
  const out = [0, 0, 0];
  for (const s of c) out[s.n - 1]! += s.k;
  while (out.length > 1 && out[out.length - 1] === 0) out.pop();
  return out;
}

/** électrons de valence (dernière couche occupée), période, bloc, colonne — pour un ATOME */
export function position(Z: number) {
  const c = configuration(Z);
  const last = c[c.length - 1]!;
  const n = last.n;
  const valence = c.filter((s) => s.n === n).reduce((s, x) => s + x.k, 0);
  const block = last.l;
  const column = Z === 2 ? 18 : block === 's' ? valence : 10 + valence;
  return { valence, period: n, block, column };
}

/** ion stable prévu (règle du gaz noble le plus proche), null si rien de simple */
export function stableIon(Z: number): number | null {
  const { valence, column } = position(Z);
  if (column === 18) return null;
  if (column === 1 || column === 2) return valence;
  if (Z === 13) return 3;
  if (column === 16 || column === 17) return valence - 8;
  return null;
}

export const NOBLE: Record<number, string> = { 2: 'hélium', 10: 'néon', 18: 'argon' };

export interface AtomInput {
  Z: number;
  A: number;
  /** nombre d'électrons */
  e: number;
}

export const charge = (a: AtomInput) => a.Z - a.e;

/** « Cl⁻ », « Mg²⁺ », « Na » */
export function speciesText(Z: number, q: number): string {
  const s = symbolOf(Z);
  if (q === 0) return s;
  const n = Math.abs(q) === 1 ? '' : sup(Math.abs(q));
  return `${s}${n}${q > 0 ? '⁺' : '⁻'}`;
}

/* ---------- noyau ---------- */

export type Decay = 'alpha' | 'beta-' | 'beta+';

export interface Nucleus {
  Z: number;
  A: number;
}

export function decay(n: Nucleus, d: Decay): Nucleus | null {
  if (d === 'alpha') return n.Z > 2 && n.A - 4 >= n.Z - 2 ? { Z: n.Z - 2, A: n.A - 4 } : null;
  if (d === 'beta-') return n.A > n.Z && n.Z < MAX_Z ? { Z: n.Z + 1, A: n.A } : null;
  return n.Z > 1 ? { Z: n.Z - 1, A: n.A } : null;
}

export const nucleusText = (n: Nucleus) => `${symbolOf(n.Z)}-${n.A}`;

/**
 * Mode de désintégration RÉEL des noyaux proposés (famille de l'uranium 238, et les noyaux
 * fils des autres exemples) : on ne laisse choisir que ce que fait vraiment le noyau.
 */
const REAL: Record<string, Decay | 'stable'> = {
  '92-238': 'alpha',
  '90-234': 'beta-',
  '91-234': 'beta-',
  '92-234': 'alpha',
  '90-230': 'alpha',
  '88-226': 'alpha',
  '86-222': 'alpha',
  '84-218': 'alpha',
  '82-214': 'beta-',
  '83-214': 'beta-',
  '84-214': 'alpha',
  '82-210': 'beta-',
  '83-210': 'beta-',
  '84-210': 'alpha',
  '82-206': 'stable',
  '6-14': 'beta-',
  '7-14': 'stable',
  '27-60': 'beta-',
  '28-60': 'stable',
  '53-131': 'beta-',
  '54-131': 'stable',
  '9-18': 'beta+',
  '8-18': 'stable',
};

/** mode réel du noyau, « stable », ou null s'il n'est pas répertorié */
export const realMode = (n: Nucleus): Decay | 'stable' | null => REAL[`${n.Z}-${n.A}`] ?? null;

export const decayLabel = (d: Decay) => (d === 'alpha' ? 'α' : d === 'beta-' ? 'β⁻' : 'β⁺');

/** équation (texte) : « ²³⁸₉₂U → ²³⁴₉₀Th + ⁴₂He » */
const SUBD = '₀₁₂₃₄₅₆₇₈₉';
const subs = (n: number) =>
  (n < 0 ? '₋' : '') + String(Math.abs(n)).replace(/\d/g, (d) => SUBD[Number(d)]!);
export const nuclide = (A: number, Z: number, s: string) => `${sup(A)}${subs(Z)}${s}`;

export function equationText(n: Nucleus, d: Decay): string | null {
  const f = decay(n, d);
  if (!f) return null;
  const emitted =
    d === 'alpha' ? nuclide(4, 2, 'He') : d === 'beta-' ? nuclide(0, -1, 'e') : nuclide(0, 1, 'e');
  return `${nuclide(n.A, n.Z, symbolOf(n.Z))} → ${nuclide(f.A, f.Z, symbolOf(f.Z))} + ${emitted}`;
}

export const PRESETS: ReadonlyArray<{ label: string; Z: number; A: number }> = [
  { label: 'Uranium 238', Z: 92, A: 238 },
  { label: 'Radium 226', Z: 88, A: 226 },
  { label: 'Carbone 14', Z: 6, A: 14 },
  { label: 'Cobalt 60', Z: 27, A: 60 },
  { label: 'Iode 131', Z: 53, A: 131 },
  { label: 'Fluor 18', Z: 9, A: 18 },
];

/* ---------- phrases du prof ---------- */

export function atomLines(a: AtomInput, rank: number | null): string[] {
  const q = charge(a);
  const N = a.A - a.Z;
  const name = nameOf(a.Z);
  const out: string[] = [];
  out.push(
    `${a.Z} proton${a.Z > 1 ? 's' : ''} : c’est l’élément ${name} (Z = ${a.Z}). ${N} neutron${N > 1 ? 's' : ''} : A = Z + N = ${a.A} nucléons.`,
  );
  out.push(
    q === 0
      ? `${a.e} électron${a.e > 1 ? 's' : ''}, autant que de protons : l’atome est électriquement neutre.`
      : q > 0
        ? `${a.e} électrons pour ${a.Z} protons : ${q} électron${q > 1 ? 's' : ''} en moins, c’est un ion positif (cation) ${speciesText(a.Z, q)}. Le noyau n’a pas changé.`
        : `${a.e} électrons pour ${a.Z} protons : ${-q} électron${q < -1 ? 's' : ''} en plus, c’est un ion négatif (anion) ${speciesText(a.Z, q)}. Le noyau n’a pas changé.`,
  );
  if (rank === null || rank >= 5) {
    out.push(`Configuration électronique : ${configText(a.e)}.`);
    const p = position(a.Z);
    if (q === 0)
      out.push(
        `Atome : ${p.valence} électron${p.valence > 1 ? 's' : ''} de valence (couche n = ${p.period}) : période ${p.period}, bloc ${p.block}, colonne ${p.column}.`,
      );
    const ion = stableIon(a.Z);
    if (ion !== null) {
      const nob = NOBLE[a.Z - ion];
      out.push(
        `Ion stable prévu : ${speciesText(a.Z, ion)}, qui a la configuration du ${nob ?? 'gaz noble le plus proche'} (${configText(a.Z - ion)}).`,
      );
    }
    out.push(
      `Masse ≈ A × m(nucléon) = ${a.A} × 1,67 × 10⁻²⁷ ≈ ${fr(a.A * 1.67, 2)} × 10⁻²⁷ kg (les électrons comptent peu).`,
    );
  } else {
    out.push(
      'Le noyau est environ 100 000 fois plus petit que l’atome (10⁻¹⁵ m contre 10⁻¹⁰ m) ; entre les deux, du vide.',
    );
  }
  if (ISOTOPES[a.Z] && ISOTOPES[a.Z]!.length > 1)
    out.push(
      `Isotopes du ${name} : ${ISOTOPES[a.Z]!.map((x) => `${name} ${x}`).join(', ')} : même Z, nombres de neutrons différents.`,
    );
  return out;
}

export function nucleusLines(n: Nucleus, last: { from: Nucleus; d: Decay } | null): string[] {
  const out = [
    `${nuclide(n.A, n.Z, symbolOf(n.Z))} : ${n.Z} protons et ${n.A - n.Z} neutrons (${nameOf(n.Z)} ${n.A}).`,
  ];
  const mode = realMode(n);
  if (mode === 'stable') out.push('Ce noyau est stable : il ne se désintègre plus.');
  else if (mode) out.push(`Ce noyau est radioactif, émetteur ${decayLabel(mode)}.`);
  if (last) {
    const eq = equationText(last.from, last.d)!;
    out.push(`Désintégration ${decayLabel(last.d)} : ${eq}.`);
    out.push(
      last.d === 'alpha'
        ? `Conservation : A = ${last.from.A} = ${n.A} + 4 ; Z = ${last.from.Z} = ${n.Z} + 2.`
        : last.d === 'beta-'
          ? `Un neutron du noyau devient un proton : Z = ${last.from.Z} = ${n.Z} − 1, A ne change pas (${n.A}).`
          : `Un proton du noyau devient un neutron : Z = ${last.from.Z} = ${n.Z} + 1, A ne change pas (${n.A}).`,
    );
    out.push('Le noyau fils, souvent excité, se désexcite en émettant un photon γ.');
  } else out.push('Fais-le se désintégrer : A et Z se conservent.');
  return out;
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'composition'
  | 'ion-electrons'
  | 'neutral'
  | 'sizes'
  | 'element'
  | 'notation'
  | 'isotopes'
  | 'config'
  | 'ion-predict'
  | 'position'
  | 'alpha'
  | 'beta-minus'
  | 'beta-plus'
  | 'conservation'
  | 'chain';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  if (rank === 4) return ['composition', 'ion-electrons', 'neutral', 'sizes', 'element'];
  if (rank === 5) return ['notation', 'isotopes', 'config', 'ion-predict', 'position'];
  if (rank === 7) return ['alpha', 'beta-minus', 'beta-plus', 'conservation', 'chain'];
  return ['composition', 'notation', 'config', 'alpha', 'beta-minus'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({
  kind,
  question,
  options: mcqOptions(good, wrongs, rng),
  explain,
});

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'composition': {
      const [Z, A] = pick(
        [
          [6, 12],
          [8, 16],
          [11, 23],
          [13, 27],
          [17, 35],
          [26, 56],
        ] as const,
        rng,
      );
      return q(
        kind,
        `Un atome de ${nameOf(Z)} a ${Z} protons et ${A} nucléons. Combien a-t-il de neutrons et d’électrons ?`,
        `${A - Z} neutrons et ${Z} électrons`,
        [
          [
            `${A} neutrons et ${Z} électrons`,
            'Les nucléons comptent protons ET neutrons : N = A − Z.',
          ],
          [
            `${A - Z} neutrons et ${A - Z} électrons`,
            'L’atome est neutre : autant d’électrons que de PROTONS.',
          ],
          [
            `${Z} neutrons et ${A - Z} électrons`,
            'N = A − Z neutrons ; Z électrons (autant que de protons).',
          ],
        ],
        `N = A − Z = ${A} − ${Z} = ${A - Z} neutrons ; atome neutre : ${Z} électrons, autant que de protons.`,
        rng,
      );
    }
    case 'ion-electrons': {
      const [Z, qq] = pick(
        [
          [29, 2],
          [26, 3],
          [17, -1],
          [8, -2],
          [12, 2],
          [11, 1],
        ] as const,
        rng,
      );
      const e = Z - qq;
      return q(
        kind,
        `Combien d’électrons possède l’ion ${speciesText(Z, qq)} ? (Z = ${Z})`,
        `${e}`,
        [
          [
            `${Z + qq}`,
            qq > 0
              ? 'Un ion positif a PERDU des électrons : il en a moins que de protons.'
              : 'Un ion négatif a GAGNÉ des électrons : il en a plus que de protons.',
          ],
          [`${Z}`, 'Un ion n’a pas autant d’électrons que de protons : sinon il serait neutre.'],
          [
            `${Math.abs(qq)}`,
            'La charge indique le nombre d’électrons perdus ou gagnés, pas le nombre total.',
          ],
        ],
        `${speciesText(Z, qq)} : ${Z} protons, charge ${qq > 0 ? '+' : '−'}${Math.abs(qq)}, donc ${Z} ${qq > 0 ? '−' : '+'} ${Math.abs(qq)} = ${e} électrons.`,
        rng,
      );
    }
    case 'neutral':
      return q(
        kind,
        'Pourquoi un atome est-il électriquement neutre ?',
        'Il a autant d’électrons que de protons',
        [
          [
            'Il a autant de neutrons que de protons',
            'Les neutrons ne sont pas chargés : ils ne compensent rien.',
          ],
          ['Ses électrons ne sont pas chargés', 'Les électrons sont chargés négativement.'],
          ['Son noyau n’est pas chargé', 'Le noyau est chargé positivement (les protons).'],
        ],
        'Charge du noyau : +Z (protons) ; charge des électrons : −Z. Elles se compensent : l’atome est neutre.',
        rng,
      );
    case 'sizes':
      return q(
        kind,
        'Le noyau d’un atome mesure environ 10⁻¹⁵ m et l’atome 10⁻¹⁰ m. Combien de fois l’atome est-il plus grand que son noyau ?',
        '100 000 fois',
        [
          [
            '5 fois',
            'On ne soustrait pas les exposants comme des nombres ordinaires : 10⁻¹⁰ ÷ 10⁻¹⁵ = 10⁵.',
          ],
          ['1 000 fois', '10⁻¹⁰ ÷ 10⁻¹⁵ = 10⁵, pas 10³.'],
          ['Ils ont la même taille', 'Le noyau est minuscule : l’atome est surtout du vide.'],
        ],
        '10⁻¹⁰ ÷ 10⁻¹⁵ = 10⁵ = 100 000 : l’atome est essentiellement vide (structure lacunaire).',
        rng,
      );
    case 'element': {
      const Z = pick([6, 8, 26, 29], rng);
      return q(
        kind,
        `Un noyau contient ${Z} protons. Quel élément est-ce ?`,
        `${nameOf(Z)[0]!.toUpperCase()}${nameOf(Z).slice(1)} (${symbolOf(Z)})`,
        [6, 7, 8, 26, 27, 29, 30]
          .filter((x) => x !== Z)
          .slice(0, 3)
          .map(
            (x) =>
              [
                `${nameOf(x)[0]!.toUpperCase()}${nameOf(x).slice(1)} (${symbolOf(x)})`,
                `${nameOf(x)} a Z = ${x}.`,
              ] as W,
          ),
        `Le numéro atomique Z (nombre de protons) identifie l’élément : Z = ${Z}, c’est le ${nameOf(Z)}.`,
        rng,
      );
    }
    case 'notation': {
      const [Z, A] = pick(
        [
          [17, 35],
          [17, 37],
          [26, 56],
          [6, 14],
          [92, 238],
          [11, 23],
        ] as const,
        rng,
      );
      const s = nuclide(A, Z, symbolOf(Z));
      return q(
        kind,
        `Combien de neutrons contient le noyau ${s} ?`,
        `${A - Z}`,
        [
          [`${A}`, 'A compte TOUS les nucléons : protons + neutrons.'],
          [`${Z}`, 'Z est le nombre de protons.'],
          [`${A + Z}`, 'N = A − Z.'],
        ],
        `Dans ᴬ_Z X, A = ${A} nucléons et Z = ${Z} protons : N = A − Z = ${A - Z} neutrons.`,
        rng,
      );
    }
    case 'isotopes':
      return q(
        kind,
        'Le carbone 12 et le carbone 14 sont deux isotopes. Ils ont…',
        'Le même nombre de protons, mais pas le même nombre de neutrons',
        [
          [
            'Le même nombre de neutrons, mais pas le même nombre de protons',
            'Même élément = même Z (protons).',
          ],
          ['Le même nombre de nucléons', 'A diffère : 12 et 14.'],
          [
            'Des nombres d’électrons différents à l’état d’atome',
            'Atomes neutres de même Z : même nombre d’électrons (6).',
          ],
        ],
        'Isotopes : même Z (même élément), A différents, donc nombres de neutrons différents (6 et 8 ici).',
        rng,
      );
    case 'config': {
      const Z = pick([7, 8, 11, 13, 16, 17], rng);
      const good = configText(Z);
      const wrong1 = configText(Z).replace(/2p⁶ 3s(\S+)/, '2p⁸ 3s$1');
      return q(
        kind,
        `Configuration électronique de l’atome de ${nameOf(Z)} (Z = ${Z}) ?`,
        good,
        [
          [configText(Z + 1), `Compte bien : l’atome a ${Z} électrons, autant que de protons.`],
          [configText(Z - 1), `Compte bien : l’atome a ${Z} électrons.`],
          [
            wrong1 !== good ? wrong1 : configText(Z + 2),
            'Une sous-couche p contient au plus 6 électrons, une s au plus 2.',
          ],
        ],
        `${Z} électrons, dans l’ordre 1s, 2s, 2p, 3s, 3p (2 électrons au plus dans s, 6 dans p) : ${good}.`,
        rng,
      );
    }
    case 'ion-predict': {
      const Z = pick([3, 11, 12, 9, 17, 8, 16, 13], rng);
      const ion = stableIon(Z)!;
      const good = speciesText(Z, ion);
      return q(
        kind,
        `Quel ion monoatomique stable forme le ${nameOf(Z)} (Z = ${Z}) ?`,
        good,
        [
          [
            speciesText(Z, -ion),
            ion > 0
              ? 'Avec peu d’électrons de valence, l’atome en PERD : ion positif.'
              : 'Il lui manque peu d’électrons : il en GAGNE, ion négatif.',
          ],
          [
            speciesText(Z, ion > 0 ? ion + 1 : ion - 1),
            'Il prend la configuration du gaz noble le plus proche, ni plus ni moins.',
          ],
          [
            speciesText(Z, 0) + ' (pas d’ion)',
            `Le ${nameOf(Z)} n’est pas un gaz noble : il forme un ion.`,
          ],
        ],
        `${configText(Z)} : l’atome ${ion > 0 ? `perd ${ion} électron${ion > 1 ? 's' : ''}` : `gagne ${-ion} électron${ion < -1 ? 's' : ''}`} pour prendre la configuration du ${NOBLE[Z - ion]} : ${good}.`,
        rng,
      );
    }
    case 'position': {
      const Z = pick([5, 7, 9, 12, 15, 16, 17], rng);
      const p = position(Z);
      const good = `Période ${p.period}, colonne ${p.column}`;
      return q(
        kind,
        `${nameOf(Z)[0]!.toUpperCase()}${nameOf(Z).slice(1)} : ${configText(Z)}. Où est-il dans le tableau périodique ?`,
        good,
        [
          [
            `Période ${p.period}, colonne ${p.valence}`,
            `Bloc p : colonne = 10 + nombre d’électrons de valence.`,
          ],
          [
            `Période ${p.period + 1}, colonne ${p.column}`,
            'La période est le numéro n de la dernière couche occupée.',
          ],
          [
            `Période ${p.period}, colonne ${p.column === 18 ? 17 : p.column + 1}`,
            `Compte les électrons de toute la couche n = ${p.period} (s ET p).`,
          ],
        ].filter(([t]) => t !== good) as W[],
        `Dernière couche n = ${p.period} : période ${p.period} ; ${p.valence} électrons de valence en bloc ${p.block} : colonne ${p.column}.`,
        rng,
      );
    }
    case 'alpha': {
      const [Z, A] = pick(
        [
          [92, 238],
          [88, 226],
          [84, 210],
          [95, 241],
          [86, 222],
        ] as const,
        rng,
      );
      const f = decay({ Z, A }, 'alpha')!;
      return q(
        kind,
        `${nuclide(A, Z, symbolOf(Z))} est émetteur α. Quel est le noyau fils ?`,
        nuclide(f.A, f.Z, symbolOf(f.Z)),
        [
          [
            nuclide(A - 2, Z - 2, symbolOf(Z - 2)),
            'Une particule α est un noyau d’hélium ⁴₂He : A diminue de 4.',
          ],
          [
            nuclide(A - 4, Z - 4, symbolOf(Z - 4)),
            'Z diminue de 2 (2 protons dans ⁴₂He), pas de 4.',
          ],
          [
            nuclide(A, Z - 2, symbolOf(Z - 2)),
            'A diminue aussi : la particule α emporte 4 nucléons.',
          ],
        ],
        `Conservation : A = ${A} = A’ + 4 et Z = ${Z} = Z’ + 2, d’où ${nuclide(f.A, f.Z, symbolOf(f.Z))} (${nameOf(f.Z)}).`,
        rng,
      );
    }
    case 'beta-minus': {
      const [Z, A] = pick(
        [
          [6, 14],
          [27, 60],
          [53, 131],
          [55, 137],
          [38, 90],
          [15, 32],
        ] as const,
        rng,
      );
      const f = decay({ Z, A }, 'beta-')!;
      return q(
        kind,
        `${nuclide(A, Z, symbolOf(Z))} est émetteur β⁻. Quel est le noyau fils ?`,
        nuclide(f.A, f.Z, symbolOf(f.Z)),
        [
          [
            nuclide(A, Z - 1, symbolOf(Z - 1)),
            'β⁻ : un neutron devient un proton, Z AUGMENTE de 1.',
          ],
          [nuclide(A - 1, Z + 1, symbolOf(Z + 1)), 'L’électron émis a A = 0 : A ne change pas.'],
          [nuclide(A, Z, symbolOf(Z)), 'Le noyau change d’élément : Z augmente de 1.'],
        ],
        `${nuclide(A, Z, symbolOf(Z))} → ${nuclide(f.A, f.Z, symbolOf(f.Z))} + ${nuclide(0, -1, 'e')} : A conservé, Z = ${Z} + 1.`,
        rng,
      );
    }
    case 'beta-plus': {
      const [Z, A] = pick(
        [
          [9, 18],
          [8, 15],
          [7, 13],
          [11, 22],
        ] as const,
        rng,
      );
      const f = decay({ Z, A }, 'beta+')!;
      return q(
        kind,
        `${nuclide(A, Z, symbolOf(Z))} est émetteur β⁺. Quel est le noyau fils ?`,
        nuclide(f.A, f.Z, symbolOf(f.Z)),
        [
          [
            nuclide(A, Z + 1, symbolOf(Z + 1)),
            'β⁺ : un proton devient un neutron, Z DIMINUE de 1.',
          ],
          [nuclide(A - 1, Z - 1, symbolOf(Z - 1)), 'Le positon émis a A = 0 : A ne change pas.'],
          [nuclide(A - 4, Z - 2, symbolOf(Z - 2)), 'C’est une désintégration α, pas β⁺.'],
        ],
        `${nuclide(A, Z, symbolOf(Z))} → ${nuclide(f.A, f.Z, symbolOf(f.Z))} + ${nuclide(0, 1, 'e')} : A conservé, Z = ${Z} − 1.`,
        rng,
      );
    }
    case 'conservation':
      return q(
        kind,
        'Lors d’une désintégration radioactive, qu’est-ce qui se conserve ?',
        'Le nombre de nucléons A et le nombre de charge Z',
        [
          [
            'Seulement le nombre de protons',
            'Z total se conserve, mais le noyau peut perdre des protons (α) ou en gagner (β⁻).',
          ],
          ['L’élément chimique', 'Le noyau fils est en général un autre élément.'],
          [
            'Le nombre de neutrons du noyau',
            'En β⁻, un neutron devient un proton : le nombre de neutrons change.',
          ],
        ],
        'Lois de conservation : la somme des A et la somme des Z sont les mêmes avant et après (particules émises comprises).',
        rng,
      );
    case 'chain': {
      return q(
        kind,
        'L’uranium ²³⁸₉₂U aboutit au plomb ²⁰⁶₈₂Pb par une suite de désintégrations α et β⁻. Combien de désintégrations α ?',
        '8',
        [
          ['32', 'Chaque α enlève 4 nucléons : (238 − 206) ÷ 4.'],
          [
            '10',
            'Seules les α changent A : (238 − 206) ÷ 4 = 8 ; ce n’est pas la différence des Z.',
          ],
          ['6', '6, c’est le nombre de β⁻ (pour rattraper Z), pas de α.'],
        ],
        'Les β⁻ ne changent pas A : 238 − 206 = 32 = 4 × 8, donc 8 α (Z baisse de 16) ; puis 92 − 16 + x = 82 donne x = 6 β⁻.',
        rng,
      );
    }
  }
}

export const nb = NB;
